import { useState, useEffect, useCallback } from 'react';
import './CircuitDebugger.css';

// Types
interface Variable {
  name: string;
  value: string;
  type: string;
  changed?: boolean;
}

interface StackFrame {
  name: string;
  line: number;
  file: string;
}

interface Breakpoint {
  id: string;
  line: number;
  condition?: string;
  enabled: boolean;
  hitCount: number;
}

interface ExecutionStep {
  step: number;
  line: number;
  operation: string;
  duration_us: number;
  gasUsed: number;
}

interface DebugSession {
  id: string;
  circuitId: string;
  circuitName: string;
  status: 'paused' | 'running' | 'completed' | 'error';
  currentLine: number;
  currentStep: number;
  totalSteps: number;
  variables: Variable[];
  callStack: StackFrame[];
  executionHistory: ExecutionStep[];
}

// Mock circuit source
const MOCK_CIRCUIT_SOURCE = `// aggregate-v1.2.3
// Aggregation circuit for batch processing

fn execute(input: Input) -> Output {
    let sum = 0;
    let count = input.values.len();
    
    // Process each value
    for i in 0..count {
        let value = input.values[i];
        
        // Validate value
        if value < 0 {
            return Output::error("negative value");
        }
        
        sum += value;
    }
    
    // Calculate average
    let avg = sum / count;
    
    // Generate proof
    let proof = generate_povc(sum, count, avg);
    
    Output {
        sum: sum,
        average: avg,
        count: count,
        proof: proof,
    }
}

fn generate_povc(sum: i64, count: i64, avg: i64) -> Proof {
    // PoVC proof generation
    let witness = compute_witness(sum, count, avg);
    let commitment = commit(witness);
    create_proof(commitment)
}`;

// Components
function SourceViewer({
  source,
  currentLine,
  breakpoints,
  onToggleBreakpoint,
}: {
  source: string;
  currentLine: number;
  breakpoints: Breakpoint[];
  onToggleBreakpoint: (line: number) => void;
}) {
  const lines = source.split('\n');
  const breakpointLines = new Set(breakpoints.filter((b) => b.enabled).map((b) => b.line));

  return (
    <div className="source-viewer">
      <div className="source-header">
        <span className="source-filename">aggregate-v1.2.3.escir</span>
      </div>
      <div className="source-content">
        {lines.map((line, index) => {
          const lineNum = index + 1;
          const isCurrentLine = lineNum === currentLine;
          const hasBreakpoint = breakpointLines.has(lineNum);

          return (
            <div
              key={lineNum}
              className={`source-line ${isCurrentLine ? 'current' : ''}`}
            >
              <span
                className={`line-gutter ${hasBreakpoint ? 'has-breakpoint' : ''}`}
                onClick={() => onToggleBreakpoint(lineNum)}
              >
                {hasBreakpoint && <span className="breakpoint-dot" />}
                <span className="line-number">{lineNum}</span>
              </span>
              <span className="line-content">
                {isCurrentLine && <span className="current-indicator">→</span>}
                <code>{line || ' '}</code>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VariablesPanel({ variables }: { variables: Variable[] }) {
  return (
    <div className="debug-panel">
      <div className="panel-header">Variables</div>
      <div className="panel-content">
        {variables.length === 0 ? (
          <div className="empty-state">No variables in scope</div>
        ) : (
          <table className="variables-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Value</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {variables.map((v) => (
                <tr key={v.name} className={v.changed ? 'changed' : ''}>
                  <td className="var-name">{v.name}</td>
                  <td className="var-value">{v.value}</td>
                  <td className="var-type">{v.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CallStackPanel({ stack }: { stack: StackFrame[] }) {
  return (
    <div className="debug-panel">
      <div className="panel-header">Call Stack</div>
      <div className="panel-content">
        {stack.length === 0 ? (
          <div className="empty-state">Empty stack</div>
        ) : (
          <div className="call-stack">
            {stack.map((frame, i) => (
              <div key={i} className={`stack-frame ${i === 0 ? 'current' : ''}`}>
                <span className="frame-indicator">{i === 0 ? '→' : ' '}</span>
                <span className="frame-name">{frame.name}</span>
                <span className="frame-location">:{frame.line}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BreakpointsPanel({
  breakpoints,
  onToggle,
  onRemove,
}: {
  breakpoints: Breakpoint[];
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="debug-panel">
      <div className="panel-header">Breakpoints</div>
      <div className="panel-content">
        {breakpoints.length === 0 ? (
          <div className="empty-state">No breakpoints set</div>
        ) : (
          <div className="breakpoints-list">
            {breakpoints.map((bp) => (
              <div key={bp.id} className={`breakpoint-item ${bp.enabled ? '' : 'disabled'}`}>
                <input
                  type="checkbox"
                  checked={bp.enabled}
                  onChange={() => onToggle(bp.id)}
                />
                <span className="bp-location">Line {bp.line}</span>
                {bp.condition && <span className="bp-condition">if {bp.condition}</span>}
                <span className="bp-hits">({bp.hitCount} hits)</span>
                <button className="bp-remove" onClick={() => onRemove(bp.id)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ExecutionTimeline({ steps, currentStep }: { steps: ExecutionStep[]; currentStep: number }) {
  const maxDuration = Math.max(...steps.map((s) => s.duration_us), 1);

  return (
    <div className="debug-panel execution-timeline">
      <div className="panel-header">Execution Timeline</div>
      <div className="panel-content">
        <div className="timeline-container">
          {steps.map((step) => (
            <div
              key={step.step}
              className={`timeline-step ${step.step === currentStep ? 'current' : ''} ${step.step < currentStep ? 'executed' : ''}`}
              title={`Step ${step.step}: ${step.operation} (${step.duration_us}μs)`}
            >
              <div
                className="step-bar"
                style={{ height: `${(step.duration_us / maxDuration) * 100}%` }}
              />
              <span className="step-number">{step.step}</span>
            </div>
          ))}
        </div>
        <div className="timeline-legend">
          <span>Total: {steps.reduce((acc, s) => acc + s.duration_us, 0)}μs</span>
          <span>Gas: {steps.reduce((acc, s) => acc + s.gasUsed, 0)}</span>
        </div>
      </div>
    </div>
  );
}

function DebugControls({
  status,
  onRun,
  onPause,
  onStep,
  onStepOver,
  onStepOut,
  onRestart,
}: {
  status: DebugSession['status'];
  onRun: () => void;
  onPause: () => void;
  onStep: () => void;
  onStepOver: () => void;
  onStepOut: () => void;
  onRestart: () => void;
}) {
  const isPaused = status === 'paused';
  const isFinished = status === 'completed' || status === 'error';

  return (
    <div className="debug-controls">
      <button
        className="control-btn primary"
        onClick={isPaused ? onRun : onPause}
        disabled={isFinished}
        title={isPaused ? 'Continue (F5)' : 'Pause'}
      >
        {isPaused ? '▶' : '⏸'}
      </button>
      <button
        className="control-btn"
        onClick={onStep}
        disabled={!isPaused}
        title="Step Into (F11)"
      >
        ⤵
      </button>
      <button
        className="control-btn"
        onClick={onStepOver}
        disabled={!isPaused}
        title="Step Over (F10)"
      >
        ⤳
      </button>
      <button
        className="control-btn"
        onClick={onStepOut}
        disabled={!isPaused}
        title="Step Out (Shift+F11)"
      >
        ⤴
      </button>
      <div className="control-separator" />
      <button
        className="control-btn"
        onClick={onRestart}
        title="Restart (Ctrl+Shift+F5)"
      >
        ↻
      </button>
      <div className="control-status">
        <span className={`status-indicator ${status}`} />
        <span className="status-text">{status}</span>
      </div>
    </div>
  );
}

// Main Component
export default function CircuitDebugger() {
  const [session, setSession] = useState<DebugSession | null>(null);
  const [breakpoints, setBreakpoints] = useState<Breakpoint[]>([]);
  const [selectedCircuit, setSelectedCircuit] = useState<string>('aggregate-v1.2.3');

  // Initialize mock session
  useEffect(() => {
    const mockSession: DebugSession = {
      id: 'debug-001',
      circuitId: 'circuit-abc123',
      circuitName: 'aggregate-v1.2.3',
      status: 'paused',
      currentLine: 5,
      currentStep: 3,
      totalSteps: 25,
      variables: [
        { name: 'input', value: '{ values: [1, 2, 3, 4, 5] }', type: 'Input' },
        { name: 'sum', value: '6', type: 'i64', changed: true },
        { name: 'count', value: '5', type: 'usize' },
        { name: 'i', value: '2', type: 'usize' },
        { name: 'value', value: '3', type: 'i64', changed: true },
      ],
      callStack: [
        { name: 'execute', line: 5, file: 'aggregate.escir' },
      ],
      executionHistory: Array.from({ length: 15 }, (_, i) => ({
        step: i + 1,
        line: 4 + (i % 12),
        operation: ['load', 'add', 'compare', 'branch', 'store'][i % 5],
        duration_us: Math.floor(Math.random() * 100) + 10,
        gasUsed: Math.floor(Math.random() * 50) + 5,
      })),
    };
    setSession(mockSession);
  }, []);

  const handleToggleBreakpoint = useCallback((line: number) => {
    setBreakpoints((prev) => {
      const existing = prev.find((bp) => bp.line === line);
      if (existing) {
        return prev.filter((bp) => bp.line !== line);
      }
      return [...prev, {
        id: `bp-${line}`,
        line,
        enabled: true,
        hitCount: 0,
      }];
    });
  }, []);

  const handleToggleBreakpointEnabled = useCallback((id: string) => {
    setBreakpoints((prev) =>
      prev.map((bp) => (bp.id === id ? { ...bp, enabled: !bp.enabled } : bp))
    );
  }, []);

  const handleRemoveBreakpoint = useCallback((id: string) => {
    setBreakpoints((prev) => prev.filter((bp) => bp.id !== id));
  }, []);

  const handleRun = useCallback(() => {
    if (!session) return;
    setSession({ ...session, status: 'running' });
    // Simulate running to next breakpoint
    setTimeout(() => {
      setSession((s) => s ? {
        ...s,
        status: 'paused',
        currentLine: Math.min(s.currentLine + 5, 35),
        currentStep: Math.min(s.currentStep + 3, s.totalSteps),
      } : null);
    }, 500);
  }, [session]);

  const handlePause = useCallback(() => {
    if (!session) return;
    setSession({ ...session, status: 'paused' });
  }, [session]);

  const handleStep = useCallback(() => {
    if (!session) return;
    const newLine = session.currentLine + 1;
    const newStep = session.currentStep + 1;
    
    if (newStep > session.totalSteps) {
      setSession({ ...session, status: 'completed', currentLine: 35 });
      return;
    }

    setSession({
      ...session,
      currentLine: newLine > 35 ? 35 : newLine,
      currentStep: newStep,
      variables: session.variables.map((v) => ({
        ...v,
        changed: Math.random() > 0.7,
        value: v.name === 'i' ? String(Number(v.value) + 1) : v.value,
      })),
    });
  }, [session]);

  const handleStepOver = useCallback(() => {
    handleStep();
  }, [handleStep]);

  const handleStepOut = useCallback(() => {
    if (!session) return;
    setSession({
      ...session,
      currentLine: Math.min(session.currentLine + 10, 35),
      currentStep: Math.min(session.currentStep + 5, session.totalSteps),
      callStack: session.callStack.slice(1),
    });
  }, [session]);

  const handleRestart = useCallback(() => {
    setSession((s) => s ? {
      ...s,
      status: 'paused',
      currentLine: 5,
      currentStep: 1,
      variables: [
        { name: 'input', value: '{ values: [1, 2, 3, 4, 5] }', type: 'Input' },
      ],
      callStack: [{ name: 'execute', line: 5, file: 'aggregate.escir' }],
    } : null);
  }, []);

  if (!session) {
    return (
      <div className="circuit-debugger loading">
        <div className="spinner spinner-lg" />
        <p>Loading debug session...</p>
      </div>
    );
  }

  return (
    <div className="circuit-debugger">
      <div className="debugger-header">
        <div className="circuit-selector">
          <label>Circuit:</label>
          <select value={selectedCircuit} onChange={(e) => setSelectedCircuit(e.target.value)}>
            <option value="aggregate-v1.2.3">aggregate-v1.2.3</option>
            <option value="validate-v1.0.0">validate-v1.0.0</option>
            <option value="transform-v2.1.0">transform-v2.1.0</option>
          </select>
        </div>
        <DebugControls
          status={session.status}
          onRun={handleRun}
          onPause={handlePause}
          onStep={handleStep}
          onStepOver={handleStepOver}
          onStepOut={handleStepOut}
          onRestart={handleRestart}
        />
        <div className="step-counter">
          Step {session.currentStep} / {session.totalSteps}
        </div>
      </div>

      <div className="debugger-content">
        <div className="debugger-main">
          <SourceViewer
            source={MOCK_CIRCUIT_SOURCE}
            currentLine={session.currentLine}
            breakpoints={breakpoints}
            onToggleBreakpoint={handleToggleBreakpoint}
          />
        </div>
        <div className="debugger-sidebar">
          <VariablesPanel variables={session.variables} />
          <CallStackPanel stack={session.callStack} />
          <BreakpointsPanel
            breakpoints={breakpoints}
            onToggle={handleToggleBreakpointEnabled}
            onRemove={handleRemoveBreakpoint}
          />
        </div>
      </div>

      <div className="debugger-footer">
        <ExecutionTimeline
          steps={session.executionHistory}
          currentStep={session.currentStep}
        />
      </div>
    </div>
  );
}
